import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { FileField } from "./shared";

/* ---------------- Festivals Admin ---------------- */
export default function FestivalsAdmin() {
  const [sub, setSub] = useState("festivals");
  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-white/5">
        {[
          { id: "festivals", label: "Festivals", Icon: Award },
          { id: "credentials", label: "Credentials", Icon: KeyRound },
          { id: "jury", label: "Jury", Icon: User2 },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`fest-sub-${t.id}`}
            onClick={() => setSub(t.id)}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              sub === t.id ? "border-amber-400 text-white" : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {sub === "festivals" ? <FestivalsPanel /> : sub === "credentials" ? <CredentialsAdmin /> : <JuryAdmin />}
    </div>
  );
}

function FestivalsPanel() {
  const [festivals, setFestivals] = useState([]);
  const [editing, setEditing] = useState(null); // current festival being edited, or "new" sentinel object
  const [loading, setLoading] = useState(true);
  const [submissionStats, setSubmissionStats] = useState({}); // festival_id → count

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/festival/festivals");
      setFestivals(data || []);
      // Count submissions per festival (best-effort, in parallel)
      const stats = {};
      await Promise.all((data || []).map(async (f) => {
        try {
          const { data: subs } = await api.get("/festival/submissions", { params: { festival_id: f.festival_id, limit: 500 } });
          stats[f.festival_id] = Array.isArray(subs) ? subs.length : (subs?.items?.length || 0);
        } catch { stats[f.festival_id] = 0; }
      }));
      setSubmissionStats(stats);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load festivals");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (festival_id) => {
    if (!window.confirm("Delete this festival? Submissions inside will keep their festival_id but become orphaned. This cannot be undone.")) return;
    try {
      await api.delete(`/admin/festival/festivals/${festival_id}`);
      toast.success("Festival deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-festivals-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2"><Award className="w-5 h-5 text-amber-400" /> Festivals</h2>
        <p className="text-xs text-gray-500 ml-2 hidden md:block">Create seasons, set ticket prices, toggle active status. Films keep their `festival_id` link.</p>
        <button data-testid="festival-new-btn" onClick={() => setEditing({ _new: true, name: "", slug: "", season: "", starts_at: "", ends_at: "", is_active: true, description: "", banner_url: "", ticket_price: 4.99, ticket_currency: "usd" })} className="ml-auto btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> New festival
        </button>
      </div>

      {loading && <p className="text-gray-400">Loading…</p>}

      {!loading && festivals.length === 0 && (
        <div data-testid="festivals-empty" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-10 text-center">
          <Award className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-1">No festivals yet</h3>
          <p className="text-sm text-gray-500 mb-4">Click "New festival" to launch a season — submissions and tickets will route to it.</p>
        </div>
      )}

      {!loading && festivals.length > 0 && (
        <div className="space-y-2">
          {festivals.map((f) => (
            <div key={f.festival_id} data-testid={`festival-row-${f.slug}`} className="bg-[#0f0f0f] border border-white/5 hover:border-white/15 rounded-lg p-4 flex items-center gap-4 transition-colors">
              <div className={`w-12 h-12 rounded flex items-center justify-center font-black ${f.is_active ? "bg-amber-500/20 text-amber-400 border border-amber-500/40" : "bg-white/5 text-gray-500"}`}>
                <Award className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{f.name}</span>
                  {f.is_active ? (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-2 py-0.5">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest bg-white/5 text-gray-500 border border-white/10 rounded px-2 py-0.5">Inactive</span>
                  )}
                  <span className="text-xs text-gray-500">/{f.slug}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span>{f.season || "—"}</span>
                  <span>·</span>
                  <span>${f.ticket_price?.toFixed(2)} {f.ticket_currency?.toUpperCase()}</span>
                  <span>·</span>
                  <span data-testid={`festival-subs-count-${f.slug}`}>{submissionStats[f.festival_id] ?? "…"} submissions</span>
                </div>
              </div>
              <button data-testid={`festival-edit-${f.slug}`} onClick={() => setEditing(f)} className="btn-glass px-3 py-1.5 rounded text-xs">Edit</button>
              <button data-testid={`festival-delete-${f.slug}`} onClick={() => remove(f.festival_id)} className="text-gray-500 hover:text-red-400 p-2" aria-label="delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <FestivalForm
          initial={editing}
          isNew={!!editing._new}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function FestivalForm({ initial, isNew, onClose, onSaved }) {
  const toIsoDate = (val) => {
    if (!val) return "";
    try { return new Date(val).toISOString().slice(0, 10); } catch { return ""; }
  };
  const [form, setForm] = useState({
    name: initial.name || "",
    slug: initial.slug || "",
    season: initial.season || "",
    starts_at: toIsoDate(initial.starts_at),
    ends_at: toIsoDate(initial.ends_at),
    is_active: initial.is_active !== false,
    description: initial.description || "",
    banner_url: initial.banner_url || "",
    ticket_price: typeof initial.ticket_price === "number" ? initial.ticket_price : 4.99,
    ticket_currency: initial.ticket_currency || "usd",
    submission_fee_short: typeof initial.submission_fee_short === "number" ? initial.submission_fee_short : 20,
    submission_fee_feature: typeof initial.submission_fee_feature === "number" ? initial.submission_fee_feature : 75,
    short_film_max_min: typeof initial.short_film_max_min === "number" ? initial.short_film_max_min : 25,
    submission_fee_currency: initial.submission_fee_currency || "usd",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!/^[a-z0-9-]+$/.test(form.slug)) { toast.error("Slug must be lowercase letters, numbers, dashes only"); return; }
    if (form.ticket_price < 0) { toast.error("Ticket price must be ≥ 0"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Convert YYYY-MM-DD → ISO datetime at midnight UTC; leave blank → undefined
        starts_at: form.starts_at ? new Date(form.starts_at + "T00:00:00Z").toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at + "T23:59:59Z").toISOString() : null,
      };
      if (isNew) {
        await api.post("/admin/festival/festivals", payload);
        toast.success("Festival created");
      } else {
        await api.patch(`/admin/festival/festivals/${initial.festival_id}`, payload);
        toast.success("Festival updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="festival-form-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={() => !saving && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl bg-gradient-to-br from-[#1a1208] to-[#0a0a0a] border border-amber-500/30 rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={() => !saving && onClose()} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close" disabled={saving}>
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-6 h-6 text-amber-400" />
          <h3 className="text-xl font-black">{isNew ? "New festival season" : `Edit "${initial.name}"`}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Festival name *</label>
            <input data-testid="festival-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. World Film Festival — Summer 2026" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Slug *</label>
            <input data-testid="festival-form-slug" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="wff-summer-2026" disabled={!isNew} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm disabled:opacity-50" />
            {!isNew && <p className="text-[10px] text-gray-500 mt-1">Slug is immutable — locked once submissions exist.</p>}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Season label</label>
            <input data-testid="festival-form-season" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="Summer 2026" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Starts</label>
            <input data-testid="festival-form-starts" type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Ends</label>
            <input data-testid="festival-form-ends" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Ticket price</label>
            <div className="flex gap-2">
              <input data-testid="festival-form-price" type="number" step="0.01" min="0" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: parseFloat(e.target.value) || 0 })} className="flex-1 bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
              <select data-testid="festival-form-currency" value={form.ticket_currency} onChange={(e) => setForm({ ...form, ticket_currency: e.target.value })} className="bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm">
                <option value="usd">USD</option>
                <option value="eur">EUR</option>
                <option value="gbp">GBP</option>
                <option value="cad">CAD</option>
                <option value="inr">INR</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Status</label>
            <button type="button" data-testid="festival-form-active-toggle" onClick={() => setForm({ ...form, is_active: !form.is_active })} className={`w-full px-3 py-2 rounded text-sm font-semibold border ${form.is_active ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/15 text-gray-400"}`}>
              {form.is_active ? "Active — accepting submissions" : "Inactive — closed"}
            </button>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Banner image URL</label>
            <input data-testid="festival-form-banner" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} placeholder="https://…/banner.jpg" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 -mx-1">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1.5">
              <Film className="w-3 h-3" /> Submission fees (charged to filmmakers)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Short fee ($)</label>
                <input data-testid="festival-form-fee-short" type="number" step="0.01" min="0" value={form.submission_fee_short} onChange={(e) => setForm({ ...form, submission_fee_short: parseFloat(e.target.value) || 0 })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Feature fee ($)</label>
                <input data-testid="festival-form-fee-feature" type="number" step="0.01" min="0" value={form.submission_fee_feature} onChange={(e) => setForm({ ...form, submission_fee_feature: parseFloat(e.target.value) || 0 })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">Short cutoff (min)</label>
                <input data-testid="festival-form-fee-cutoff" type="number" min="1" max="180" value={form.short_film_max_min} onChange={(e) => setForm({ ...form, short_film_max_min: parseInt(e.target.value || "25", 10) })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              Films ≤ {form.short_film_max_min} min pay <span className="text-amber-300 font-semibold">${form.submission_fee_short.toFixed(2)}</span>. Anything longer (incl. medium 26-79 min) pays <span className="text-amber-300 font-semibold">${form.submission_fee_feature.toFixed(2)}</span>. Admins can waive per-film.
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Description</label>
            <textarea data-testid="festival-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 1000) })} rows={4} placeholder="Tell filmmakers and audiences what this season is about…" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm resize-none" />
            <p className="text-[10px] text-gray-500 mt-1 text-right">{form.description.length}/1000</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-white/10">
          <button type="button" onClick={onClose} disabled={saving} className="btn-glass px-5 py-2 rounded text-sm">Cancel</button>
          <button data-testid="festival-form-save" type="submit" disabled={saving} className="btn-red px-6 py-2 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {saving ? "Saving…" : (isNew ? "Create festival" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Credentials Admin ---------------- */
function CredentialsAdmin() {
  const [creds, setCreds] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [scans, setScans] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkRole, setBulkRole] = useState("Jury");
  const [bulkPromote, setBulkPromote] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/festival/credentials${q}`)
      .then((r) => { setCreds(r.data?.credentials || []); setTotal(r.data?.total || 0); })
      .catch(() => setCreds([]));
    api.get(`/admin/festival/scans?limit=15`)
      .then((r) => setScans(r.data))
      .catch(() => setScans(null));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [statusFilter]);

  const revoke = async (cid) => {
    if (!window.confirm(`Revoke credential ${cid}? The QR will flip to RED at venues immediately.`)) return;
    try {
      await api.post(`/admin/festival/credentials/${cid}/revoke`);
      toast.success("Credential revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  const reinstate = async (cid) => {
    try {
      await api.post(`/admin/festival/credentials/${cid}/reinstate`);
      toast.success("Credential reinstated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reinstate failed"); }
  };

  const runBulk = async () => {
    const rows = bulkRaw.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const [email, ...rest] = line.split(/[,;\s]+/);
      return { email, role: bulkRole, name: rest.join(" ") || undefined };
    });
    if (!rows.length) { toast.error("Paste at least one email"); return; }
    if (rows.length > 200) { toast.error("Max 200 rows per batch"); return; }
    setBulkBusy(true);
    setBulkResult(null);
    const t = toast.loading(`Issuing ${rows.length} credential(s)…`);
    try {
      const { data } = await api.post("/admin/festival/credentials/bulk-issue", { rows, promote: bulkPromote });
      setBulkResult(data);
      toast.success(`+${data.issued} issued · ${data.reused} reused · ${data.missing} missing`, { id: t, duration: 7000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk issue failed", { id: t });
    } finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="admin-credentials-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-amber-400" /> Festival Credentials
        </h2>
        <span className="text-xs text-gray-500">{total} issued</span>
        <select
          data-testid="cred-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-dark text-sm py-1.5"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
        </select>
        <button
          data-testid="cred-bulk-open"
          onClick={() => { setBulkResult(null); setBulkOpen(true); }}
          className="ml-auto btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Bulk issue
        </button>
      </div>

      {scans && (
        <div data-testid="live-scans-panel" className="mb-6 border border-white/5 rounded-lg overflow-hidden bg-gradient-to-br from-[#0e0e0e] to-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <h3 className="font-semibold text-sm">Live venue scans</h3>
              <span className="text-[10px] uppercase tracking-widest text-gray-500">refreshes every 20s</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
            <ScanStat n={scans.last_hour ?? 0} label="Last hour" testid="scan-stat-hour" />
            <ScanStat n={scans.last_day ?? 0} label="Last 24 h" testid="scan-stat-day" />
            <ScanStat n={scans.revoked_blocked ?? 0} label="Revoked blocked" color="text-red-300" testid="scan-stat-revoked" />
            <ScanStat n={scans.tampered_blocked ?? 0} label="Tampered blocked" color="text-orange-300" testid="scan-stat-tampered" />
          </div>
          {scans.venue_leaderboard_24h?.length > 0 && (
            <div className="px-4 py-3 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Venue leaderboard (last 24h)</div>
              <div className="flex flex-wrap gap-2">
                {scans.venue_leaderboard_24h.map((v) => (
                  <span key={v.venue} data-testid={`venue-chip-${v.venue}`} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs">
                    <span className="text-gray-200">{v.venue}</span>
                    <span className="text-gray-500 ml-1.5">· {v.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {scans.scans?.length > 0 && (
            <div className="max-h-56 overflow-y-auto border-t border-white/5">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.02] text-[9px] uppercase tracking-widest text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Credential</th>
                    <th className="text-left px-4 py-2">Venue · Gate</th>
                    <th className="text-left px-4 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.scans.map((s, i) => (
                    <tr key={i} data-testid={`scan-row-${i}`} className="border-t border-white/5">
                      <td className="px-4 py-2 text-gray-400">{s.scanned_at ? new Date(s.scanned_at).toLocaleTimeString() : "—"}</td>
                      <td className="px-4 py-2 text-gray-200">
                        <div className="font-mono text-[10px] text-gray-500">{s.credential_id}</div>
                        <div>{s.name || "—"}</div>
                      </td>
                      <td className="px-4 py-2 text-gray-400">{[s.venue, s.gate].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded ${
                          s.status_at_scan === "active" ? "bg-emerald-500/15 text-emerald-300"
                          : s.status_at_scan === "revoked" ? "bg-red-500/15 text-red-300"
                          : s.status_at_scan === "tampered" ? "bg-orange-500/15 text-orange-300"
                          : "bg-gray-500/15 text-gray-300"
                        }`}>{s.status_at_scan}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!creds.length ? (
        <p className="text-sm text-gray-500 py-8 text-center">No credentials yet. Filmmakers self-issue from their dashboard, or use Bulk issue for jury/press batches.</p>
      ) : (
        <div className="border border-white/5 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Credential ID</th>
                <th className="text-left px-4 py-2.5">Issued</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {creds.map((c) => (
                <tr key={c.credential_id} data-testid={`cred-row-${c.credential_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 text-amber-300 text-xs uppercase tracking-wider">{c.role}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{c.credential_id}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{c.issued_at_display}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${
                      c.status === "active" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : "bg-red-500/15 text-red-300 border border-red-500/30"
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "active" ? (
                      <button
                        data-testid={`cred-revoke-${c.credential_id}`}
                        onClick={() => revoke(c.credential_id)}
                        className="text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 px-2.5 py-1.5 rounded inline-flex items-center gap-1"
                      >
                        <Ban className="w-3.5 h-3.5" /> Revoke
                      </button>
                    ) : (
                      <button
                        data-testid={`cred-reinstate-${c.credential_id}`}
                        onClick={() => reinstate(c.credential_id)}
                        className="text-xs text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 px-2.5 py-1.5 rounded inline-flex items-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" /> Reinstate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="cred-bulk-modal" className="bg-[#141414] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" /> Bulk-issue credentials</h3>
              <button onClick={() => !bulkBusy && setBulkOpen(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Paste one email per line. Optionally add a name after the email separated by space, comma or semicolon. Users must already exist.</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Role for batch</label>
                <select data-testid="cred-bulk-role" value={bulkRole} onChange={(e) => setBulkRole(e.target.value)} className="input-dark w-full text-sm">
                  {["Filmmaker", "Director", "Producer", "Press", "Jury"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <label className="flex items-end gap-2 text-xs text-gray-400 pb-1.5">
                <input data-testid="cred-bulk-promote" type="checkbox" checked={bulkPromote} onChange={(e) => setBulkPromote(e.target.checked)} className="mt-0.5" />
                Auto-promote to filmmaker role if not yet
              </label>
            </div>
            <textarea
              data-testid="cred-bulk-textarea"
              rows={6}
              value={bulkRaw}
              onChange={(e) => setBulkRaw(e.target.value)}
              placeholder={"jury1@studio.com Jane Director\npress2@magazine.com John Press\nproducer@company.com"}
              className="input-dark w-full font-mono text-xs mb-4"
              disabled={bulkBusy}
            />
            <div className="flex gap-3">
              <button data-testid="cred-bulk-run" onClick={runBulk} disabled={bulkBusy || !bulkRaw.trim()} className="btn-red flex-1 py-3 rounded font-semibold disabled:opacity-50">
                {bulkBusy ? "Issuing…" : "Issue credentials"}
              </button>
              <button type="button" onClick={() => !bulkBusy && setBulkOpen(false)} className="btn-glass px-6 py-3 rounded font-semibold">
                {bulkResult ? "Close" : "Cancel"}
              </button>
            </div>
            {bulkResult && (
              <div data-testid="cred-bulk-result" className="border-t border-white/5 pt-4 mt-4 space-y-2">
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">+{bulkResult.issued} issued</span>
                  <span className="px-2.5 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">{bulkResult.reused} reused</span>
                  {bulkResult.missing > 0 && <span className="px-2.5 py-1 rounded bg-gray-500/10 text-gray-300 border border-gray-500/30">{bulkResult.missing} missing</span>}
                  {bulkResult.failed > 0 && <span className="px-2.5 py-1 rounded bg-red-500/10 text-red-300 border border-red-500/30">{bulkResult.failed} failed</span>}
                </div>
                <ul className="max-h-48 overflow-y-auto bg-black/40 border border-white/5 rounded p-2 space-y-1 text-xs">
                  {bulkResult.results.map((r, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className={`w-16 text-[10px] uppercase font-bold tracking-wider ${
                        r.status === "issued" ? "text-emerald-300"
                        : r.status === "reused" ? "text-amber-300"
                        : r.status === "missing" ? "text-gray-400"
                        : "text-red-300"
                      }`}>{r.status}</span>
                      <span className="text-gray-300 flex-1 truncate">{r.email}</span>
                      <span className="text-gray-500 font-mono text-[10px] truncate">{r.credential_id || r.reason || ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ScanStat({ n, label, color = "text-white", testid }) {
  return (
    <div className="bg-[#0e0e0e] px-4 py-3">
      <div data-testid={testid} className={`text-2xl font-black tabular-nums ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

/* ---------------- Jury Admin ---------------- */
function JuryAdmin() {
  const [jury, setJury] = useState([]);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "", bio: "", achievements: "", photo_url: "", is_published: true });
  const [photoUploading, setPhotoUploading] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo too large (max 5MB)"); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "poster");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setPhotoUploading(false); }
  };

  const load = () => {
    api.get("/admin/jury")
      .then((r) => { setJury(r.data?.jury || []); setTotal(r.data?.total || 0); })
      .catch(() => setJury([]));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email.trim()) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim(),
        display_name: form.display_name || undefined,
        bio: form.bio,
        achievements: form.achievements.split("\n").map((s) => s.trim()).filter(Boolean),
        photo_url: form.photo_url || undefined,
        is_published: form.is_published,
      };
      const { data } = await api.post("/admin/jury/register", payload);
      toast.success(`Jury registered · ${data.jury.display_name}`);
      setAddOpen(false);
      setForm({ email: "", display_name: "", bio: "", achievements: "", photo_url: "", is_published: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to register jury");
    } finally { setSaving(false); }
  };

  const remove = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from jury? Their profile will be hidden and jury role revoked.`)) return;
    try {
      await api.delete(`/admin/jury/${uid}`);
      toast.success("Jury removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-jury-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <User2 className="w-5 h-5 text-amber-400" /> Festival Jury
        </h2>
        <span className="text-xs text-gray-500">{total} registered</span>
        <button data-testid="jury-add-open" onClick={() => setAddOpen(true)} className="ml-auto btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Register jury
        </button>
      </div>

      {!jury.length ? (
        <p className="text-sm text-gray-500 py-8 text-center">No jury yet. Register existing users as jury and they&rsquo;ll get a public profile + judging dashboard at /jury/dashboard.</p>
      ) : (
        <div className="border border-white/5 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Public URL</th>
                <th className="text-left px-4 py-2.5">Achievements</th>
                <th className="text-left px-4 py-2.5">Votes</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jury.map((j) => (
                <tr key={j.user_id} data-testid={`jury-row-${j.user_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {j.photo_url ? <img src={j.photo_url.startsWith("http") ? j.photo_url : `${process.env.REACT_APP_BACKEND_URL}${j.photo_url}`} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold">{(j.display_name || "?").charAt(0).toUpperCase()}</div>}
                      <div>
                        <div>{j.display_name}</div>
                        <div className="text-[10px] text-gray-500">{j.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <a href={`/jury/${j.slug}`} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline text-xs font-mono">/jury/{j.slug}</a>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{j.achievements?.length || 0}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{j.votes_cast || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${j.is_published ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-gray-500/15 text-gray-300 border border-white/10"}`}>{j.is_published ? "Public" : "Hidden"}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(j.user_id, j.display_name)} data-testid={`jury-remove-${j.user_id}`} className="text-xs text-red-300 hover:bg-red-500/10 px-2.5 py-1.5 rounded inline-flex items-center gap-1">
                      <Ban className="w-3.5 h-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="jury-add-modal" className="bg-[#141414] border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold flex items-center gap-2"><User2 className="w-5 h-5 text-amber-400" /> Register jury member</h3>
              <button onClick={() => !saving && setAddOpen(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Existing user email *</label>
                <input data-testid="jury-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jury@example.com" className="input-dark w-full text-sm" />
                <p className="text-[10px] text-gray-600 mt-1">User must already have a CineCinema account.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Display name (optional — uses account name if blank)</label>
                <input data-testid="jury-form-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="input-dark w-full text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Bio</label>
                <textarea data-testid="jury-form-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Programmer at Berlinale, founding editor of ScreenLog…" className="input-dark w-full text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Achievements (one per line, max 20)</label>
                <textarea data-testid="jury-form-achievements" rows={4} value={form.achievements} onChange={(e) => setForm({ ...form, achievements: e.target.value })} placeholder={"Berlinale Shorts Committee 2019–2024\nBest Director, IIFF 2021"} className="input-dark w-full text-sm font-mono text-xs" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Photo</label>
                <FileField
                  label="Jury photo"
                  url={form.photo_url}
                  onUrl={(u) => setForm({ ...form, photo_url: u })}
                  loading={photoUploading}
                  onFile={uploadPhoto}
                  accept="image/*"
                  testid="jury-form-photo"
                />
                {form.photo_url && (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={form.photo_url.startsWith("http") ? form.photo_url : `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`}
                      alt="preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-amber-300/40"
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photo_url: "" })}
                      className="text-xs text-red-300 hover:text-red-200"
                    >Remove photo</button>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input data-testid="jury-form-published" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                Publish public profile at /jury/{'{slug}'}
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="jury-form-submit" onClick={submit} disabled={saving} className="btn-red flex-1 py-3 rounded font-semibold disabled:opacity-50">{saving ? "Registering…" : "Register jury"}</button>
              <button type="button" onClick={() => setAddOpen(false)} className="btn-glass px-6 py-3 rounded font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

